import { useTranslate } from '@/i18n';
import {
  ChangeEventHandler,
  Dialog,
  DialogContent,
  DialogTitle,
  PinCodeControl,
} from 'ui-kit';
import { Typography } from '@mui/material';
import { inheritDataTestId } from 'ui-kit/lib/utils/dataTestId';
import { ResendPinCode } from './ResendPinCode.tsx';
import React, { useEffect, useState } from 'react';
import { usePin } from '@/store/sessions/hooks.ts';
import { SessionStep } from '@/store/sessions/types.ts';
import { useAppDispatch, useAppSelector } from '@/store/store.ts';
import { cancel } from '@/store/sessions/slice.ts';

export const PinDialog = ({ ...props }) => {
  const step = useAppSelector((d) => d.sessions.step);
  const dispatch = useAppDispatch();
  return (
    <Dialog
      open={step === SessionStep.Pin}
      size={'xs'}
      data-test-id={inheritDataTestId(props)}
      onClose={() => {
        dispatch(cancel());
      }}
    >
      <PinDialogContent />
    </Dialog>
  );
};

const AUTO_SUBMIT_TIMEOUT = 1000;

export interface PinDialogContentProps {}
const PinDialogContent = ({ ...props }: PinDialogContentProps) => {
  const {
    error,
    loading,
    resetError,
    submitPin,
    dismiss,
    resendPin,
    resendTime,
  } = usePin();

  const { t } = useTranslate('profile');
  const [pin, setPin] = useState<string[]>([]);

  const handleChange: ChangeEventHandler<string[]> = (e, s) => {
    if (error) resetError();
    setPin(s);
  };

  return (
    <>
      <DialogTitle
        onClose={dismiss}
        data-test-id={inheritDataTestId(props, 'title')}
      >
        {t('ENTER_PIN_CODE')}
      </DialogTitle>
      <DialogContent pt={24} pb={36}>
        <Typography
          sx={{
            typography: { xs: 'string/md' },
            marginBottom: 24,
          }}
        >
          {t('A_SIX_DIGIT_PIN_CODE_HAS_BEEN_SENT_TO_YOUR_PHONE_')}
        </Typography>
        <PinCodeControl
          error={t(error)}
          disabled={loading}
          value={pin}
          onChange={handleChange}
          data-test-id={inheritDataTestId(props, 'pin-code-control')}
          length={6}
          label={t('PIN_CODE')}
          sx={{ marginBottom: 18 }}
        />
        <ResendPinCode
          reSend={() => {
            void resendPin();
            setPin([]);
          }}
          timeMs={resendTime}
        />
        <AutoSubmit onSubmit={submitPin} pin={pin} />
      </DialogContent>
    </>
  );
};

const AutoSubmit = ({
  onSubmit,
  pin,
}: {
  onSubmit: (pin: string) => void;
  pin: string[];
}) => {
  useEffect(() => {
    let pinCode: ReturnType<typeof setTimeout>;
    if (pin.join('').length === 6) {
      pinCode = setTimeout(() => {
        onSubmit(pin.join(''));
      }, AUTO_SUBMIT_TIMEOUT);
    }
    return () => {
      clearTimeout(pinCode);
    };
  }, [onSubmit, pin]);
  return null;
};
